import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Download, Edit2, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import Layout from './layout';
import { examineeGroupData } from '../data/examineeGroupMockData';
import ExamineeGroupModal from '../Modal/ExamineeGroupModal';
import EditMemberModal from '../Modal/EditMemberModal';
import GroupingModal from '../Modal/GroupingModal';
import { memberAPI } from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Custom styles สำหรับ DatePicker
const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    font-family: 'Noto Sans Thai', sans-serif;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__header {
    background-color: #00b5b8;
    border-bottom: none;
    padding-top: 0.75rem;
    border-radius: 0.5rem 0.5rem 0 0;
  }

  .react-datepicker__current-month {
    color: white;
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  .react-datepicker__day-name {
    color: white;
    font-size: 0.75rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.15rem;
  }

  .react-datepicker__day {
    width: 2rem;
    line-height: 2rem;
    margin: 0.15rem;
    font-size: 0.75rem;
    color: #374151;
    border-radius: 0.25rem;
    transition: all 0.2s;

    &:hover {
      background-color: #e0f7f8;
      color: #00b5b8;
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #00b5b8;
    color: white;
    font-weight: 500;

    &:hover {
      background-color: #0891b2;
    }
  }

  .react-datepicker__day--disabled {
    color: #d1d5db;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }

  .react-datepicker__day--today {
    font-weight: 600;
    color: #00b5b8;
    background-color: #e0f7f8;

    &:hover {
      background-color: #b3e5e8;
    }
  }

  .react-datepicker__day--outside-month {
    color: #d1d5db;
  }

  .react-datepicker__navigation {
    top: 0.75rem;
  }

  .react-datepicker__navigation-icon::before {
    border-color: white;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

// Styled Components
const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
`;

const UploadAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const UploadArea = styled.div`
  width: 100%;
  max-width: 500px;
  border: 2px dashed #D1D5DB;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #FAFAFA;

  &:hover {
    border-color: #00B5B8;
    background-color: #E0F7F8;
    transform: scale(1.02);
  }
`;

const UploadText = styled.p`
  color: #6B7280;
  font-size: 0.875rem;
  margin: 0;
`;

const UploadInput = styled.input`
  display: none;
`;

const DownloadExampleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00B5B8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;

  &:hover {
    color: #0891B2;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  accent-color: #00B5B8;
  cursor: pointer;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DateInputContainer = styled.div`
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;


const AddGroupButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;


const ResetButton = styled.button`
  background-color: #F9FAFB;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const SearchButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #333333;
  }
`;

const GroupingButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #0891B2;
  }
`;

const RightButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00B5B8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  &:hover {
    color: #0891B2;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.th`
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background-color: #F9FAFB;
  white-space: nowrap;
  min-width: ${props => props.minWidth || 'auto'};
`;

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  font-size: 12px;
  color: #6B7280;
  border-bottom: 1px solid #E5E7EB;
  white-space: nowrap;
  text-align: center;
  min-width: ${props => props.minWidth || 'auto'};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6B7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  padding: 0;

  &:hover {
    color: #374151;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  background-color: ${props => props.active ? '#1976D2' : 'transparent'};
  color: ${props => props.active ? 'white' : '#1976D2'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  min-width: 40px;
  text-decoration: ${props => props.active ? 'none' : 'underline'};

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#1565C0' : '#E3F2FD'};
  }

  &:disabled {
    color: #9E9E9E;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const ExamineeGroup = () => {
  // ค่าที่กำลังพิมพ์ในช่องค้นหา (ยังไม่ได้กดค้นหา)
  const [searchFilters, setSearchFilters] = useState({
    keyword: '',
    branch: '',
    startDate: null,
    endDate: null
  });

  // ✅ ค่าที่ใช้ในการกรองจริง (หลังกดปุ่มค้นหาแล้ว)
  const [activeFilters, setActiveFilters] = useState({
    keyword: '',
    branch: '',
    startDate: null,
    endDate: null
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminStates, setAdminStates] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGroupingModalOpen, setIsGroupingModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [companies, setCompanies] = useState([]);

  // ดึงข้อมูลสมาชิกและบริษัทจาก API (ครั้งแรกเท่านั้น)
  useEffect(() => {
    fetchMembers();
    fetchCompanies();
  }, []); // เรียกครั้งเดียวตอน mount

  // Scroll to top เมื่อเปลี่ยนหน้า
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const fetchCompanies = async () => {
    try {
      const campaignAPI = require('../services/api').campaignAPI;
      const response = await campaignAPI.getList(1, '');
      if (response?.list && Array.isArray(response.list)) {
        setCompanies(response.list);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      // 🔥 ดึงหน้าแรกเพื่อดูว่ามีทั้งหมดกี่หน้า
      const firstPageResponse = await memberAPI.getList({ pageNow: 1 });

      let allMemberData = [];
      let totalPages = 1;

      // API ส่งกลับมาในรูปแบบ { result: true, responseText: "ทำรายการสำเร็จ", list: [...], amount: "133", pageAmount: 7 }
      if (firstPageResponse.result && firstPageResponse.list && Array.isArray(firstPageResponse.list)) {
        allMemberData = [...firstPageResponse.list];
        totalPages = parseInt(firstPageResponse.pageAmount) || 1;

        // 🔥 ถ้ามีมากกว่า 1 หน้า ให้วนดึงข้อมูลทุกหน้า
        if (totalPages > 1) {
          // สร้าง array ของ promises เพื่อเรียก API พร้อมกัน
          const pagePromises = [];
          for (let page = 2; page <= totalPages; page++) {
            pagePromises.push(memberAPI.getList({ pageNow: page }));
          }

          // รอให้ทุก API call เสร็จ
          const responses = await Promise.all(pagePromises);

          // รวมข้อมูลจากทุกหน้า
          responses.forEach(response => {
            if (response.result && response.list && Array.isArray(response.list)) {
              allMemberData = [...allMemberData, ...response.list];
            }
          });
        }
      } else if (firstPageResponse.data && Array.isArray(firstPageResponse.data)) {
        allMemberData = firstPageResponse.data;
      } else if (Array.isArray(firstPageResponse)) {
        allMemberData = firstPageResponse;
      } else {
        // ถ้า API ส่งรูปแบบไม่ตรง ใช้ mock data
        console.warn('API response format unknown, using mock data');
        allMemberData = examineeGroupData;
      }

      let memberData = allMemberData;

      // ดึงข้อมูล Campaign เพื่อ map ชื่อบริษัท
      const campaignAPI = require('../services/api').campaignAPI;
      const campaignResponse = await campaignAPI.getList(1, '');
      const campaigns = campaignResponse?.list || [];

      // สร้าง map จาก campaignid -> campaign name
      const campaignMap = {};
      campaigns.forEach(camp => {
        campaignMap[camp.id] = camp.name || 'ทั่วไป';
      });

      // เพิ่ม campaign name ให้กับแต่ละ member
      memberData = memberData.map(member => {
        // ลองหา campaignid จากหลายแหล่ง
        let campaignId = member.memberCampaign?.[0]?.campaignid || '0';

        // ถ้า campaignid เป็น "0" ให้ลองใช้ค่าจาก memberCampaign[0].name ที่ไม่ใช่ "ทั่วไป"
        if (campaignId === '0' && member.memberCampaign?.[0]?.name && member.memberCampaign[0].name !== 'ทั่วไป') {
          // ถ้ามี name อยู่แล้ว ใช้เลย
          return {
            ...member,
            campaign: member.memberCampaign[0].name
          };
        }

        const campaignName = campaignMap[campaignId] || 'ทั่วไป';
        return {
          ...member,
          campaign: campaignName
        };
      });

      setMembers(memberData);

      // Set initial admin states (ใช้ level แทน isAdmin)
      const initialAdminStates = memberData.reduce((acc, item) => {
        acc[item.id] = item.level === '2'; // level "2" = admin บริษัท
        return acc;
      }, {});
      setAdminStates(initialAdminStates);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(err.message);
      // ถ้า API error ให้ใช้ mock data
      setMembers(examineeGroupData);
      const initialAdminStates = examineeGroupData.reduce((acc, item) => {
        acc[item.id] = item.isAdmin;
        return acc;
      }, {});
      setAdminStates(initialAdminStates);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filter ฝั่ง frontend ทั้งหมด (ชื่อ-สกุล, บริษัท, วันที่)
  // ใช้ activeFilters (ค่าที่กดค้นหาแล้ว) ไม่ใช่ searchFilters (ค่าที่กำลังพิมพ์)
  const currentData = members.filter(member => {
    // 1. กรองตามชื่อ-สกุล หรือ เลขบัตร
    if (activeFilters.keyword && activeFilters.keyword.trim() !== '') {
      const keyword = activeFilters.keyword.toLowerCase().trim();

      // ค้นหาจากชื่อ
      const name = (member.name || '').toLowerCase();
      const lastname = (member.lastname || '').toLowerCase();
      const nameprefix = (member.nameprefix || '').toLowerCase();
      const fullName = `${nameprefix} ${name} ${lastname}`.toLowerCase();

      // ค้นหาจาก username
      const username = (member.username || '').toLowerCase();

      // ค้นหาจากเลขบัตรประชาชน
      const nid = (member.nid || '').toLowerCase();

      const matchKeyword = fullName.includes(keyword) ||
             name.includes(keyword) ||
             lastname.includes(keyword) ||
             username.includes(keyword) ||
             nid.includes(keyword);

      if (!matchKeyword) return false;
    }

    // 2. กรองตามบริษัท
    if (activeFilters.branch && activeFilters.branch.trim() !== '') {
      const selectedCompany = activeFilters.branch.trim();
      const memberCompany = (member.campaign || '').trim();

      if (memberCompany !== selectedCompany) return false;
    }

    // 3. กรองตามวันที่เริ่มต้น
    if (activeFilters.startDate) {
      const memberDate = member.registrationDate || member.dateadd;
      if (memberDate) {
        const memberDateOnly = memberDate.split(' ')[0]; // YYYY-MM-DD
        const startDateOnly = activeFilters.startDate; // YYYY-MM-DD string
        if (memberDateOnly < startDateOnly) return false;
      } else {
        // ถ้าไม่มีวันที่ ให้กรองออก
        return false;
      }
    }

    // 4. กรองตามวันที่สิ้นสุด
    if (activeFilters.endDate) {
      const memberDate = member.registrationDate || member.dateadd;
      if (memberDate) {
        const memberDateOnly = memberDate.split(' ')[0]; // YYYY-MM-DD
        const endDateOnly = activeFilters.endDate; // YYYY-MM-DD string
        if (memberDateOnly > endDateOnly) return false;
      } else {
        // ถ้าไม่มีวันที่ ให้กรองออก
        return false;
      }
    }

    return true;
  });

  // คำนวณ pagination จากข้อมูลที่ filter แล้ว
  const totalFilteredItems = currentData.length;
  const totalPages = Math.ceil(totalFilteredItems / itemsPerPage);

  // ดึงข้อมูลเฉพาะหน้าปัจจุบัน
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = currentData.slice(startIndex, endIndex);

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    // ✅ เมื่อกดค้นหา ให้เอาค่าจาก searchFilters ไปใช้จริงใน activeFilters
    // แปลง Date object เป็น string format YYYY-MM-DD โดยใช้ local timezone
    const formatDateToYYYYMMDD = (date) => {
      if (!date) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const newFilters = {
      keyword: searchFilters.keyword,
      branch: searchFilters.branch,
      startDate: formatDateToYYYYMMDD(searchFilters.startDate),
      endDate: formatDateToYYYYMMDD(searchFilters.endDate)
    };

    setActiveFilters(newFilters);
    setCurrentPage(1); // รีเซ็ตกลับไปหน้า 1 เมื่อค้นหา
  };

  const handleReset = () => {
    // ล้างทั้งช่องค้นหาและค่าที่ใช้กรอง
    setSearchFilters({
      keyword: '',
      branch: '',
      startDate: null,
      endDate: null
    });
    setActiveFilters({
      keyword: '',
      branch: '',
      startDate: null,
      endDate: null
    });
    setCurrentPage(1);
  };

  const handleDownloadCSV = () => {
    // ฟังก์ชันแปลงเพศ (Backend: 0 = หญิง, 1 = ชาย)
    const getGender = (gender) => {
      // แปลง type ให้เป็น string ก่อนเปรียบเทียบ
      const genderStr = String(gender ?? '').trim();
      if (genderStr === '1' || genderStr.toLowerCase() === 'male' || genderStr === 'ชาย') return 'ชาย';
      if (genderStr === '0' || genderStr.toLowerCase() === 'female' || genderStr === 'หญิง') return 'หญิง';
      if (genderStr === '') return '-'; // ถ้าไม่มีข้อมูลให้แสดง -
      return 'อื่นๆ';
    };

    // ฟังก์ชันคำนวณอายุจากวันเกิด
    const calculateAge = (birthday) => {
      if (!birthday) return '-';

      let birthDate = new Date(birthday);

      // ตรวจสอบว่าวันที่ valid หรือไม่
      if (isNaN(birthDate.getTime())) {
        return '-';
      }

      const today = new Date();
      let birthYear = birthDate.getFullYear();

      // ถ้าปี >= 2500 แสดงว่าเป็น พ.ศ. ต้องแปลงเป็น ค.ศ.
      if (birthYear >= 2500) {
        birthYear = birthYear - 543;
        birthDate = new Date(birthYear, birthDate.getMonth(), birthDate.getDate());
      }

      let age = today.getFullYear() - birthYear;
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      // ถ้าอายุติดลบหรือมากกว่า 120 ให้แสดง - (ปรับจาก 150 เป็น 120 เพื่อความสมเหตุสมผล)
      if (age < 0 || age > 120) {
        return '-';
      }

      return age;
    };

    // ฟังก์ชันแปลงวันที่ให้เป็น DD/MM/YYYY
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = dateString.split(' ')[0]; // เอาแค่ส่วนวันที่
      const parts = date.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`; // DD/MM/YYYY
      }
      return date;
    };

    // สร้าง CSV header
    const headers = ['ลำดับ', 'วันที่สมัคร', 'เลขบัตรประชาชน', 'ชื่อ-สกุล', 'บริษัท', 'แผนก', 'ตำแหน่ง'];

    // ✅ ใช้ currentData เพื่อ export ทั้งหมดตาม filter ที่เลือก
    const dataToExport = currentData;

    const rows = dataToExport.map((member, index) => {
      const rowNumber = index + 1;
      const registrationDate = formatDate(member.registrationDate || member.dateadd);
      // เพิ่ม ="..." เพื่อบังคับให้ Excel แสดงเป็น text (ป้องกัน scientific notation)
      const nid = member.nid ? `="${member.nid}"` : '-';
      const fullName = member.name ? `${member.nameprefix || ''} ${member.name} ${member.lastname || ''}`.trim() : '-';
      const company = member.campaign || '-';
      const department = member.position || '-'; // แผนก
      const position = member.job || '-'; // ตำแหน่ง

      return [rowNumber, registrationDate, nid, fullName, company, department, position];
    });

    // รวม header และ rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => {
        // Escape คำที่มี comma, quotes หรือ newline
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
      .join('\n');

    // เพิ่ม BOM สำหรับ UTF-8 เพื่อให้ Excel เปิดภาษาไทยได้ถูกต้อง
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // สร้าง download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `รายชื่อสมาชิก_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadExample = () => {
    console.log('ดาวน์โหลดตัวอย่าง v1');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      console.log('อัปโหลดไฟล์:', file.name);
    }
  };

  const handleUploadClick = () => {
    document.getElementById('file-upload').click();
  };

  const handleAdminToggle = async (id) => {
    const newState = !adminStates[id];

    // Update local state immediately for better UX
    setAdminStates(prev => ({
      ...prev,
      [id]: newState
    }));

    try {
      // Find the member and update via API
      const member = members.find(m => m.id === id);
      if (member) {
        await memberAPI.update({
          ...member,
          level: newState ? '2' : '1' // level "2" = admin บริษัท, "1" = user ทั่วไป
        });
      }
    } catch (err) {
      console.error('Error updating admin status:', err);
      // Revert state on error
      setAdminStates(prev => ({
        ...prev,
        [id]: !newState
      }));
      alert('เกิดข้อผิดพลาดในการอัพเดทสถานะ: ' + err.message);
    }
  };

  const handleSaveMember = async (memberData) => {
    try {
      if (memberData.id) {
        // แก้ไขสมาชิก
        await memberAPI.update(memberData);
      } else {
        // เพิ่มสมาชิกใหม่
        await memberAPI.add(memberData);
      }
      fetchMembers(); // รีเฟรชข้อมูล
      setIsModalOpen(false);
      setEditingMember(null);
    } catch (err) {
      console.error('Error saving member:', err);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + err.message);
    }
  };

  const handleUpdateMember = async (memberData) => {
    try {
      // แก้ไขสมาชิก
      const response = await memberAPI.update(memberData);

      // ตรวจสอบว่า API ตอบกลับสำเร็จหรือไม่
      if (response.result) {
        // ✅ เก็บข้อมูลบริษัทที่อัพเดตลง localStorage สำหรับหน้าสมุดสุขภาพ
        // หาชื่อบริษัทจาก campaignid ที่เลือก
        if (memberData.campaignid) {
          // หาชื่อบริษัทจาก companies list โดยใช้ campaignid
          const selectedCompany = companies.find(c => c.id === memberData.campaignid);
          const companyName = selectedCompany?.name;

          // ใช้ memberid หรือ id (ตาม API structure)
          const memberId = editingMember?.memberid || editingMember?.id || memberData.id;

          // ถ้ามีการเปลี่ยนบริษัท ให้เก็บลง localStorage
          if (companyName && memberId) {
            const updatedCompanies = JSON.parse(localStorage.getItem('updatedMemberCompanies') || '{}');
            updatedCompanies[memberId] = companyName;
            localStorage.setItem('updatedMemberCompanies', JSON.stringify(updatedCompanies));
          }
        }

        // Refresh ข้อมูลจาก Backend
        await fetchMembers();

        setIsEditModalOpen(false);
        setEditingMember(null);

        alert('บันทึกข้อมูลสำเร็จ');
      } else {
        const errorMsg = response.responseText || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
        console.error('API returned error:', errorMsg);
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Error updating member:', err);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + err.message);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingMember(null);
  };

  const handleSaveGrouping = async (groupingData) => {
    try {
      console.log('จัดกลุ่มผู้รับการตรวจ:', groupingData);
      // TODO: เรียก API เพื่อจัดกลุ่ม
      // await memberAPI.groupMembers(groupingData);

      alert('จัดกลุ่มผู้รับการตรวจสำเร็จ');
      fetchMembers(); // รีเฟรชข้อมูล
    } catch (err) {
      console.error('Error grouping members:', err);
      alert('เกิดข้อผิดพลาดในการจัดกลุ่ม: ' + err.message);
    }
  };

  return (
    <Layout>
      <DatePickerStyles />
      <PageContainer>
        {/* Loading & Error States */}
        {loading && <div style={{ textAlign: 'center', padding: '1rem', fontSize: '12px' }}>กำลังโหลดข้อมูล...</div>}
        {error && <div style={{ textAlign: 'center', padding: '1rem', color: '#EF4444', fontSize: '12px' }}>เกิดข้อผิดพลาด: {error}</div>}

        {/* <UploadSection>
          <UploadAreaContainer>
            <UploadArea onClick={handleUploadClick}>
              <UploadInput
                id="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
              />
              <UploadText>
                {selectedFile ? `เลือกไฟล์: ${selectedFile.name}` : 'คลิกเพื่อเพิ่มกลุ่มผู้รับการตรวจสุขภาพ'}
              </UploadText>
            </UploadArea>
            <DownloadExampleButton onClick={handleDownloadExample}>
              <Download size={16} />
              ดาวน์โหลดตัวอย่าง v1
            </DownloadExampleButton>
          </UploadAreaContainer>
          <AddGroupButton onClick={() => setIsModalOpen(true)}>
            เพิ่มกลุ่มผู้รับการตรวจ
          </AddGroupButton>
        </UploadSection> */}

        <FilterSection style={{ marginBottom: '1rem' }}>
          <FilterGroup style={{ flex: '1', minWidth: '250px' }}>
            <Label>ค้นหาจาก ชื่อ-สกุล หรือ เลขบัตร</Label>
            <TextInput
              type="text"
              value={searchFilters.keyword}
              onChange={(e) => handleFilterChange('keyword', e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </FilterGroup>

          <FilterGroup style={{ flex: '1', minWidth: '200px' }}>
            <Label>บริษัท</Label>
            <SelectInput
              value={searchFilters.branch}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
            >
              <option value="">ทั้งหมด</option>
              {companies
                .filter((company) => company.name && company.name !== '-')
                .map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
            </SelectInput>
          </FilterGroup>

          <FilterGroup style={{ flex: '1', minWidth: '180px' }}>
            <Label>วันที่เริ่มต้น</Label>
            <DateInputContainer>
              <DatePicker
                selected={searchFilters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </FilterGroup>

          <FilterGroup style={{ flex: '1', minWidth: '180px' }}>
            <Label>ถึงวันที่</Label>
            <DateInputContainer>
              <DatePicker
                selected={searchFilters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
                minDate={searchFilters.startDate}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </FilterGroup>

          <SearchButton onClick={handleSearch}>
            ค้นหา
          </SearchButton>
        </FilterSection>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
          <DownloadButton onClick={handleDownloadCSV}>
            <Download size={16} />
            ดาวน์โหลด CSV
          </DownloadButton>

          <GroupingButton onClick={() => setIsGroupingModalOpen(true)}>
            จัดกลุ่มผู้รับการตรวจ
          </GroupingButton>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <RightButtonGroup>
            <PaginationContainer style={{ marginTop: '0.5rem' }}>
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                previous
              </PaginationButton>
              {[...Array(totalPages)].map((_, index) => (
                <PaginationButton
                  key={index + 1}
                  active={currentPage === index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </PaginationButton>
              ))}
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                next
              </PaginationButton>
            </PaginationContainer>
          </RightButtonGroup>
        </div>
      </PageContainer>


      <div style={{ overflowX: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <TableHeader minWidth="60px">ที่</TableHeader>
              <TableHeader minWidth="120px">วันที่สมัคร</TableHeader>
              <TableHeader minWidth="140px">บัตรประชาชน</TableHeader>
              <TableHeader minWidth="150px">ชื่อ-สกุล</TableHeader>
              <TableHeader minWidth="120px">บริษัท</TableHeader>
              <TableHeader minWidth="100px">แผนก</TableHeader>
              <TableHeader minWidth="100px">ตำแหน่ง</TableHeader>
              <TableHeader minWidth="80px">admin</TableHeader>
              <TableHeader minWidth="80px">แก้ไข</TableHeader>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => {
              // แปลงวันที่จาก YYYY-MM-DD เป็น DD-MM-YYYY
              const formatDate = (dateString) => {
                if (!dateString) return '-';
                const date = dateString.split(' ')[0]; // เอาแค่ส่วนวันที่ ตัด timestamp ออก
                const parts = date.split('-');
                if (parts.length === 3) {
                  return `${parts[2]}-${parts[1]}-${parts[0]}`; // DD-MM-YYYY
                }
                return date;
              };

              // แปลงเพศ: 1 = ชาย, 2 = หญิง, อื่นๆ
              const getGender = (gender) => {
                if (gender === '1') return 'ชาย';
                if (gender === '2') return 'หญิง';
                return 'อื่นๆ';
              };

              const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;

              return (
                <tr key={row.id}>
                  <TableCell minWidth="60px">{rowNumber}</TableCell>
                  <TableCell minWidth="120px">{formatDate(row.registrationDate || row.dateadd)}</TableCell>
                  <TableCell minWidth="100px">{row.nid || '-'}</TableCell>
                  <TableCell minWidth="150px">{row.name ? `${row.nameprefix || ''} ${row.name} ${row.lastname || ''}`.trim() : '-'}</TableCell>
                  <TableCell minWidth="120px">{row.campaign || '-'}</TableCell>
                  <TableCell minWidth="100px">{row.position || '-'}</TableCell>
                  <TableCell minWidth="100px">{row.job || '-'}</TableCell>
                  <TableCell minWidth="80px">
                    <input
                      type="checkbox"
                      checked={adminStates[row.id]}
                      disabled={true}
                      style={{
                        cursor: 'not-allowed',
                        transform: 'scale(1.0)'
                      }}
                    />
                  </TableCell>
                  <TableCell minWidth="80px">
                    <EditButton onClick={() => handleEditMember(row)}>
                      <Edit2 size={12} />
                    </EditButton>
                  </TableCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <ExamineeGroupModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveMember}
        memberData={null}
      />

      <EditMemberModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleUpdateMember}
        memberData={editingMember}
      />

      <GroupingModal
        isOpen={isGroupingModalOpen}
        onClose={() => setIsGroupingModalOpen(false)}
        onSave={handleSaveGrouping}
      />

    </Layout>
  );
};


export default ExamineeGroup;